/**
 * The Stock List thank-you message on `/stock-list/complete`
 * (`pages/stock-list/complete/index.tsx`): a heading, then the text with the brand's contact email
 * as a `mailto:` link. It is presentation only; the page itself sends the PayPal acknowledgement
 * that completes the order.
 */
import React from "react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { Link } from "@ryerson/frontend.navigation";
import { getBrandInfo } from "@ryerson/frontend.common";

/** Thank-you copy, from `content/ryerson/stock-list/complete.ts` → `ThankYou`. */
export type StockListThankYouContent = {
	title: string;
	/**
	 * Rendered with the contact email directly after it, so it ends in a lead-in such as
	 * "contact us: ".
	 */
	description: string;
};

/** Props of {@link StockListThankYouHero}. */
export type StockListThankYouProps = {
	content: StockListThankYouContent;
};

// The brand's contact email from `@ryerson/frontend.common`, read once when the module loads.
const emailAddress = getBrandInfo().email;

/**
 * Renders the thank-you heading and text, with separate desktop (`lg` and up) and mobile
 * layouts.
 */
const StockListThankYouHero: React.FC<StockListThankYouProps> = ({ content }) => {
	const { theme } = useTheme();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary">
					<Typography
						variant="h1"
						css={css`
							letter-spacing: -2px;
							font-size: 75px;
							line-height: 70px;
							box-sizing: border-box;
							margin-top: 15px;
							margin-bottom: 15px;
						`}
						type="tertiary"
						color={theme.colors.primary.lighterGray}
					>
						{content.title}
					</Typography>
				</ContentSection>
				<ContentSection type="primary">
					<Typography
						variant="p"
						size="md"
						css={css`
							margin-bottom: 78px;
							display: block;
							width: calc(100% - 380px);
							min-width: 520px;
							height: auto;
						`}
						type="secondary"
						color={theme.colors.primary.darkerGray}
					>
						{content.description}
						<Link
							next={false}
							to={`mailto:${emailAddress}`}
							color={theme.colors.primary.link}
						>
							{emailAddress}
						</Link>
					</Typography>
				</ContentSection>
			</Media>

			<Media lessThan="lg">
				<ContentSection type="tertiary">
					<Typography
						variant="h3"
						css={css`
							letter-spacing: -2px;
							max-width: 100%;
						`}
						type="primary"
						color={theme.colors.primary.lighterGray}
					>
						{content.title}
					</Typography>
				</ContentSection>
				<ContentSection type="secondary">
					<Typography
						variant="p"
						size="md"
						type="tertiary"
						color={theme.colors.primary.darkerGray}
					>
						{content.description}
						<Link
							next={false}
							to={`mailto:${emailAddress}`}
							color={theme.colors.primary.link}
						>
							{emailAddress}
						</Link>
					</Typography>
				</ContentSection>
			</Media>
		</>
	);
};

export default StockListThankYouHero;
